/* @flow */

// eslint-disable-next-line import/no-unresolved
import produce from 'immer';
import * as types from 'src/shared/app/notification/action/uiNotificationActionTypes';
import {
  getNotificationId,
  getNotificationData,
} from 'src/shared/app/notification/util/notificationUtil';
import { createReducer } from 'src/shared/core/util/reducerUtils';

// Utils
import { getBraindateTopic } from 'src/shared/domain/braindate/util/braindateUtil';
import { getTopicId } from '@braindate/domain/lib/topic/util';

// Types
import type { Notification } from 'src/shared/app/notification/type/notificationTypes';
import type { Topic } from '@braindate/domain/lib/topic/type';

/*
|------------------------------------------------------------------------------
| INITIAL STATE
|------------------------------------------------------------------------------
*/

const initialState = {
  queue: [],
};

/*
|------------------------------------------------------------------------------
| REDUCERS
|------------------------------------------------------------------------------
*/

const _queueNotification = (state: Object, action: Object): Object => {
  const { queue } = state;
  const { notification: newNotification } = action;

  if (
    queue.find(
      (notification) =>
        getNotificationId(notification) === getNotificationId(newNotification),
    )
  ) {
    return state;
  }

  return {
    ...state,
    queue: [...queue, { ...newNotification, active: true }],
  };
};

const _dismissNotification = (state: Object, action: Object): Object => {
  const { queue } = state;
  const { notification } = action;
  const id =
    typeof notification === 'string'
      ? notification
      : getNotificationId(notification);
  const index = queue.findIndex((n) => getNotificationId(n) === id);

  if (index < 0) {
    return state;
  }

  return produce(state, (draft) => {
    draft.queue[index].active = false;
  });
};

const _dismissTopicNotifications = (
  state: { queue: Array<Notification<Topic>> },
  action: { topic: string | Topic },
): Object => {
  const { queue } = state;
  const { topic } = action;

  const topicId = typeof topic === 'string' ? topic : getTopicId(topic);

  const filteredNotifications = queue.filter((n) => {
    const notificationData = getNotificationData(n);
    if (!notificationData) return true;

    try {
      return getTopicId(getBraindateTopic(notificationData)) !== topicId;
    } catch (err) {
      return true;
    }
  });

  return { ...state, queue: filteredNotifications };
};

/*
|------------------------------------------------------------------------------
| REDUCER
|------------------------------------------------------------------------------
*/

export default createReducer(initialState, {
  [types.QUEUE_NOTIFICATION]: _queueNotification,
  [types.DISMISS_NOTIFICATION]: _dismissNotification,
  [types.DISMISS_TOPIC_NOTIFICATIONS]: _dismissTopicNotifications,
});
